import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  MapPin, Building2, Download, Trash2, Plus, LogOut, Loader2, 
  CheckCircle, XCircle, Search, FolderOpen, X, Star, Crown, Edit
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [token, setToken] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Dialog states
  const [addCategoryOpen, setAddCategoryOpen] = useState(false);
  const [editCategoryOpen, setEditCategoryOpen] = useState(false);
  const [deleteBusinessId, setDeleteBusinessId] = useState(null);
  const [deleteCategoryId, setDeleteCategoryId] = useState(null);
  const [featureBusinessId, setFeatureBusinessId] = useState(null);
  const [featureDuration, setFeatureDuration] = useState(30);
  const [newCategory, setNewCategory] = useState({ name: "", description: "" });
  const [editCategory, setEditCategory] = useState({ id: "", name: "", description: "" });
  const [savingCategory, setSavingCategory] = useState(false);
  const [savingFeature, setSavingFeature] = useState(false);

  useEffect(() => {
    const storedToken = localStorage.getItem('adminToken');
    if (!storedToken) {
      navigate('/admin');
      return;
    }
    setToken(storedToken);
    fetchData(storedToken);
  }, [navigate]);

  const fetchData = async (authToken) => {
    setLoading(true);
    try {
      const [bizRes, catRes] = await Promise.all([
        axios.get(`${API}/admin/businesses?token=${authToken}`),
        axios.get(`${API}/categories`)
      ]);
      setBusinesses(bizRes.data);
      setCategories(catRes.data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin');
      } else {
        toast.error("Erreur lors du chargement des données");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/logout?token=${token}`);
    } catch (e) {
      // Ignore errors
    }
    localStorage.removeItem('adminToken');
    navigate('/admin');
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/admin/export?token=${token}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'annuaire_polynesien.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Export réussi !");
    } catch (e) {
      toast.error("Erreur lors de l'export");
    }
  };

  const handleDeleteBusiness = async () => {
    if (!deleteBusinessId) return;
    
    try {
      await axios.delete(`${API}/admin/businesses/${deleteBusinessId}?token=${token}`);
      setBusinesses(prev => prev.filter(b => b.id !== deleteBusinessId));
      toast.success("Entreprise supprimée");
    } catch (e) {
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleteBusinessId(null);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.name.trim()) {
      toast.error("Le nom est requis");
      return;
    }

    setSavingCategory(true);
    try {
      const res = await axios.post(`${API}/admin/categories?token=${token}`, newCategory);
      setCategories(prev => [...prev, res.data]);
      setNewCategory({ name: "", description: "" });
      setAddCategoryOpen(false);
      toast.success("Catégorie ajoutée");
    } catch (e) {
      toast.error("Erreur lors de l'ajout");
    } finally {
      setSavingCategory(false);
    }
  };

  const handleEditCategory = async () => {
    if (!editCategory.name.trim()) {
      toast.error("Le nom est requis");
      return;
    }

    setSavingCategory(true);
    try {
      await axios.put(`${API}/admin/categories/${editCategory.id}?token=${token}`, {
        name: editCategory.name,
        description: editCategory.description
      });
      setCategories(prev => prev.map(c => 
        c.id === editCategory.id 
          ? { ...c, name: editCategory.name, description: editCategory.description }
          : c
      ));
      setEditCategoryOpen(false);
      toast.success("Catégorie modifiée");
    } catch (e) {
      toast.error("Erreur lors de la modification");
    } finally {
      setSavingCategory(false);
    }
  };

  const openEditCategory = (category) => {
    setEditCategory({
      id: category.id,
      name: category.name,
      description: category.description || ""
    });
    setEditCategoryOpen(true);
  };

  const handleDeleteCategory = async () => {
    if (!deleteCategoryId) return;
    
    try {
      await axios.delete(`${API}/admin/categories/${deleteCategoryId}?token=${token}`);
      setCategories(prev => prev.filter(c => c.id !== deleteCategoryId));
      toast.success("Catégorie supprimée");
    } catch (e) {
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleteCategoryId(null);
    }
  };

  const handleSetFeatured = async () => {
    if (!featureBusinessId) return;
    
    setSavingFeature(true);
    try {
      await axios.post(`${API}/admin/businesses/${featureBusinessId}/feature?token=${token}`, {
        duration_days: featureDuration
      });
      setBusinesses(prev => prev.map(b => 
        b.id === featureBusinessId ? { ...b, is_featured: true } : b
      ));
      toast.success(`Entreprise mise en avant pour ${featureDuration} jours`);
    } catch (e) {
      toast.error("Erreur lors de la mise en avant");
    } finally {
      setSavingFeature(false);
      setFeatureBusinessId(null);
      setFeatureDuration(30);
    }
  };

  const handleRemoveFeatured = async (businessId) => {
    try {
      await axios.delete(`${API}/admin/businesses/${businessId}/feature?token=${token}`);
      setBusinesses(prev => prev.map(b => 
        b.id === businessId ? { ...b, is_featured: false } : b
      ));
      toast.success("Mise en avant retirée");
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const filteredBusinesses = businesses.filter(b => 
    b.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    b.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-lagoon animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-lagoon-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="home-logo">
            <div className="w-10 h-10 rounded-full bg-lagoon flex items-center justify-center">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <span className="font-serif text-xl font-semibold text-lagoon-900">Annuaire Polynésien</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500">Administration</span>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleLogout}
              className="text-coral border-coral hover:bg-coral/10"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-28 pb-16 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
              <div>
                <h1 className="font-serif text-3xl font-bold text-lagoon-900">
                  Tableau de bord
                </h1>
                <p className="text-gray-600 mt-1">
                  Gérez les entreprises et catégories de l'annuaire
                </p>
              </div>
              <Button onClick={handleExport} className="btn-primary" data-testid="export-btn">
                <Download className="w-4 h-4 mr-2" />
                Exporter en Excel
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="glass-card rounded-2xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-lagoon-50 flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-lagoon" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-lagoon-900">{businesses.length}</p>
                    <p className="text-gray-500 text-sm">Entreprises</p>
                  </div>
                </div>
              </div>
              <div className="glass-card rounded-2xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-lagoon-900">
                      {businesses.filter(b => b.is_verified).length}
                    </p>
                    <p className="text-gray-500 text-sm">Vérifiées</p>
                  </div>
                </div>
              </div>
              <div className="glass-card rounded-2xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-golden/20 flex items-center justify-center">
                    <Crown className="w-6 h-6 text-golden" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-lagoon-900">
                      {businesses.filter(b => b.is_featured).length}
                    </p>
                    <p className="text-gray-500 text-sm">En vedette</p>
                  </div>
                </div>
              </div>
              <div className="glass-card rounded-2xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-coral/10 flex items-center justify-center">
                    <FolderOpen className="w-6 h-6 text-coral" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-lagoon-900">{categories.length}</p>
                    <p className="text-gray-500 text-sm">Catégories</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="businesses" className="w-full">
              <TabsList className="grid w-full max-w-md grid-cols-2 mb-6">
                <TabsTrigger value="businesses" data-testid="tab-businesses">Entreprises</TabsTrigger>
                <TabsTrigger value="categories" data-testid="tab-categories">Catégories</TabsTrigger>
              </TabsList>

              {/* Businesses Tab */}
              <TabsContent value="businesses">
                <div className="glass-card rounded-2xl overflow-hidden">
                  <div className="p-6 border-b border-gray-100">
                    <div className="relative max-w-md">
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Rechercher une entreprise..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-12 h-12 bg-white/50 border-lagoon-100 rounded-xl"
                        data-testid="search-businesses"
                      />
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-lagoon-50">
                          <TableHead className="font-semibold text-lagoon-900">Nom</TableHead>
                          <TableHead className="font-semibold text-lagoon-900">Email</TableHead>
                          <TableHead className="font-semibold text-lagoon-900">Téléphone</TableHead>
                          <TableHead className="font-semibold text-lagoon-900">Catégorie</TableHead>
                          <TableHead className="font-semibold text-lagoon-900">Statut</TableHead>
                          <TableHead className="font-semibold text-lagoon-900">Vedette</TableHead>
                          <TableHead className="font-semibold text-lagoon-900 text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredBusinesses.length > 0 ? (
                          filteredBusinesses.map(business => (
                            <TableRow key={business.id} className="hover:bg-lagoon-50/50">
                              <TableCell className="font-medium">{business.name}</TableCell>
                              <TableCell>{business.email}</TableCell>
                              <TableCell>{business.phone}</TableCell>
                              <TableCell>
                                <span className="px-2 py-1 bg-lagoon-50 text-lagoon-900 rounded text-xs">
                                  {business.category_name}
                                </span>
                              </TableCell>
                              <TableCell>
                                {business.is_verified ? (
                                  <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                                    <CheckCircle className="w-4 h-4" />
                                    Vérifié
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-coral text-sm">
                                    <XCircle className="w-4 h-4" />
                                    En attente
                                  </span>
                                )}
                              </TableCell>
                              <TableCell>
                                {business.is_featured ? (
                                  <button
                                    onClick={() => handleRemoveFeatured(business.id)}
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-golden/20 text-golden rounded-full text-xs font-semibold hover:bg-golden/30 transition-colors"
                                    data-testid={`remove-featured-${business.id}`}
                                  >
                                    <Star className="w-3 h-3 fill-golden" />
                                    En vedette
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => setFeatureBusinessId(business.id)}
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs hover:bg-golden/20 hover:text-golden transition-colors"
                                    data-testid={`set-featured-${business.id}`}
                                  >
                                    <Star className="w-3 h-3" />
                                    Mettre en avant
                                  </button>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-coral hover:text-coral-600 hover:bg-coral/10"
                                  onClick={() => setDeleteBusinessId(business.id)}
                                  data-testid={`delete-business-${business.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center py-10 text-gray-500">
                              Aucune entreprise trouvée
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </TabsContent>

              {/* Categories Tab */}
              <TabsContent value="categories">
                <div className="glass-card rounded-2xl overflow-hidden">
                  <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                    <h3 className="font-semibold text-lagoon-900">Gestion des catégories</h3>
                    <Button 
                      onClick={() => setAddCategoryOpen(true)} 
                      className="btn-primary"
                      data-testid="add-category-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter
                    </Button>
                  </div>

                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-lagoon-50">
                          <TableHead className="font-semibold text-lagoon-900">Nom</TableHead>
                          <TableHead className="font-semibold text-lagoon-900">Description</TableHead>
                          <TableHead className="font-semibold text-lagoon-900 text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {categories.map(category => (
                          <TableRow key={category.id} className="hover:bg-lagoon-50/50">
                            <TableCell className="font-medium">{category.name}</TableCell>
                            <TableCell className="text-gray-600">{category.description || '-'}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-lagoon hover:text-lagoon-600 hover:bg-lagoon/10"
                                  onClick={() => openEditCategory(category)}
                                  data-testid={`edit-category-${category.id}`}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-coral hover:text-coral-600 hover:bg-coral/10"
                                  onClick={() => setDeleteCategoryId(category.id)}
                                  data-testid={`delete-category-${category.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </motion.div>
        </div>
      </main>

      {/* Feature Business Dialog */}
      <Dialog open={!!featureBusinessId} onOpenChange={() => setFeatureBusinessId(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2">
              <Crown className="w-5 h-5 text-golden" />
              Mettre en avant
            </DialogTitle>
            <DialogDescription>
              Choisissez la durée de mise en avant pour cette entreprise
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-lagoon-900 font-semibold">Durée (jours)</Label>
            <div className="grid grid-cols-3 gap-3 mt-3">
              {[7, 30, 90].map(days => (
                <button
                  key={days}
                  onClick={() => setFeatureDuration(days)}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    featureDuration === days 
                      ? 'border-golden bg-golden/10 text-lagoon-900' 
                      : 'border-gray-200 hover:border-golden/50'
                  }`}
                  data-testid={`duration-${days}`}
                >
                  <p className="text-2xl font-bold">{days}</p>
                  <p className="text-xs text-gray-500">jours</p>
                </button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setFeatureBusinessId(null)}
            >
              Annuler
            </Button>
            <Button 
              onClick={handleSetFeatured}
              className="bg-golden text-lagoon-900 hover:bg-golden/90"
              disabled={savingFeature}
              data-testid="confirm-feature-btn"
            >
              {savingFeature ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Star className="w-4 h-4 mr-2" />}
              Mettre en avant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Category Dialog */}
      <Dialog open={addCategoryOpen} onOpenChange={setAddCategoryOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Ajouter une catégorie</DialogTitle>
            <DialogDescription>
              Créez une nouvelle catégorie pour l'annuaire
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="cat-name" className="text-lagoon-900 font-semibold">
                Nom <span className="text-coral">*</span>
              </Label>
              <Input
                id="cat-name"
                value={newCategory.name}
                onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
                className="mt-2 h-12"
                placeholder="Ex: Restauration"
                data-testid="category-name-input"
              />
            </div>
            <div>
              <Label htmlFor="cat-desc" className="text-lagoon-900 font-semibold">
                Description
              </Label>
              <Input
                id="cat-desc"
                value={newCategory.description}
                onChange={(e) => setNewCategory(prev => ({ ...prev, description: e.target.value }))}
                className="mt-2 h-12"
                placeholder="Ex: Restaurants, snacks, roulottes"
                data-testid="category-desc-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setAddCategoryOpen(false)}
              data-testid="cancel-category-btn"
            >
              Annuler
            </Button>
            <Button 
              onClick={handleAddCategory} 
              className="btn-coral"
              disabled={savingCategory}
              data-testid="save-category-btn"
            >
              {savingCategory ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog open={editCategoryOpen} onOpenChange={setEditCategoryOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Modifier la catégorie</DialogTitle>
            <DialogDescription>
              Modifiez les informations de cette catégorie
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-cat-name" className="text-lagoon-900 font-semibold">
                Nom <span className="text-coral">*</span>
              </Label>
              <Input
                id="edit-cat-name"
                value={editCategory.name}
                onChange={(e) => setEditCategory(prev => ({ ...prev, name: e.target.value }))}
                className="mt-2 h-12"
                placeholder="Ex: Restauration"
                data-testid="edit-category-name-input"
              />
            </div>
            <div>
              <Label htmlFor="edit-cat-desc" className="text-lagoon-900 font-semibold">
                Description
              </Label>
              <Input
                id="edit-cat-desc"
                value={editCategory.description}
                onChange={(e) => setEditCategory(prev => ({ ...prev, description: e.target.value }))}
                className="mt-2 h-12"
                placeholder="Ex: Restaurants, snacks, roulottes"
                data-testid="edit-category-desc-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setEditCategoryOpen(false)}
              data-testid="cancel-edit-category-btn"
            >
              Annuler
            </Button>
            <Button 
              onClick={handleEditCategory} 
              className="btn-coral"
              disabled={savingCategory}
              data-testid="save-edit-category-btn"
            >
              {savingCategory ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Business Confirmation */}
      <AlertDialog open={!!deleteBusinessId} onOpenChange={() => setDeleteBusinessId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette entreprise ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. L'entreprise sera définitivement supprimée de l'annuaire.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-business">Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteBusiness}
              className="bg-coral hover:bg-coral-600"
              data-testid="confirm-delete-business"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Category Confirmation */}
      <AlertDialog open={!!deleteCategoryId} onOpenChange={() => setDeleteCategoryId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette catégorie ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Attention : les entreprises associées à cette catégorie ne seront pas supprimées.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-category">Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteCategory}
              className="bg-coral hover:bg-coral-600"
              data-testid="confirm-delete-category"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
